import { useEffect, useRef, useState } from 'react'

export function useAudioNotifications({ account, orders, getUnactionedOrders }) {
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [previousOrderCount, setPreviousOrderCount] = useState(orders.length)
  const audioContextRef = useRef(null)
  const audioBufferRef = useRef(null)

  // Play notification sound using Web Audio API
  const playNotificationSound = async () => {
    const ctx = audioContextRef.current
    const buffer = audioBufferRef.current
    if (!ctx || !buffer) return

    // Ensure context is running (Safari suspends it)
    if (ctx.state !== 'running') {
      await ctx.resume()
    }

    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.start(0)

    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200])
    }
  }

  // Enable audio with Web Audio API
  const enableAudio = () => {
    // Create or resume AudioContext
    if (!audioContextRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      audioContextRef.current = new AudioContext()
    }

    const ctx = audioContextRef.current

    // Resume context during user gesture (required for Safari)
    ctx.resume().then(() => {
      // Load audio if not already loaded
      if (!audioBufferRef.current) {
        fetch(`/sounds/${account.kitchen_notification_sound}.mp3`)
          .then(response => response.arrayBuffer())
          .then(arrayBuffer => {
            // Use callback version for older Safari compatibility
            ctx.decodeAudioData(
              arrayBuffer,
              (buffer) => {
                audioBufferRef.current = buffer
                // Play once to confirm it works
                playNotificationSound()
              },
              () => {}
            )
          })
          .catch(() => {})
      } else {
        // Already loaded, just play
        playNotificationSound()
      }
    })

    setAudioEnabled(true)
  }

  const disableAudio = () => {
    setAudioEnabled(false)
  }

  // Keep AudioContext alive - resume on any user interaction (Safari suspends aggressively)
  useEffect(() => {
    if (!audioEnabled) return

    const resumeAudio = () => {
      const ctx = audioContextRef.current
      if (ctx && ctx.state === 'suspended') {
        ctx.resume()
      }
    }

    // Listen for any user interaction
    const events = ['click', 'touchstart', 'keydown']
    events.forEach(event => document.addEventListener(event, resumeAudio))

    return () => {
      events.forEach(event => document.removeEventListener(event, resumeAudio))
    }
  }, [audioEnabled])

  // Play sound when new order arrives (works even when locked)
  useEffect(() => {
    if (audioEnabled && orders.length > previousOrderCount) {
      playNotificationSound()
    }
    setPreviousOrderCount(orders.length)
  }, [orders.length, audioEnabled, previousOrderCount])

  // Reminder sound for unactioned orders
  useEffect(() => {
    const reminderTimeout = account.new_order_reminder_timeout
    if (!audioEnabled || !reminderTimeout || reminderTimeout <= 0) return

    const playReminder = async () => {
      const unactionedOrders = getUnactionedOrders()

      if (unactionedOrders.length > 0 && audioContextRef.current && audioBufferRef.current) {
        await playNotificationSound()
      }
    }

    const interval = setInterval(playReminder, reminderTimeout * 1000)

    return () => clearInterval(interval)
  }, [audioEnabled, account.new_order_reminder_timeout, getUnactionedOrders])

  return { audioEnabled, enableAudio, disableAudio, playNotificationSound }
}
